import axios from 'axios';
import { ISearchExtraBaseFilter, ISearchExtraFlowSetting } from '@models/search/ISearchExtraFilters';
import { ISearchRequest, ISearchResults, SearchScope } from '@models/search/search.types';
import { SearchService } from '@services/SearchService';
import { AppDispatch, RootState } from '@/store/index';
import { SearchSlice, SearchResultItem } from '@/store/reducers/SearchReducer';
import { IFieldElem } from '@models/IFormData';
import { IForms } from '@models/Forms/IForms';
import { buildResultSetKey, buildSearchQueryKey } from '@utils/searchQueryKey';
import { aiSearchHub } from '@/signalr/hubs/AISearchHub';

const SEARCH_ERROR = 'Не удалось выполнить поиск. Попробуйте ещё раз.';

/** Монотонный id запроса: по нему редьюсер отличает свежий ответ от обогнавшего себя. */
let requestCounter = 0;

/** Активный запрос области — при новом отменяется, чтобы не тянуть лишний трафик. */
const controllers = new Map<SearchScope, AbortController>();

type ScopeFetcher = (
    request: ISearchRequest,
    signal?: AbortSignal,
) => Promise<{ data: ISearchResults<SearchResultItem> }>;

const fetchers: Record<SearchScope, ScopeFetcher> = {
    docs: (request, signal) => SearchService.searchDocuments(request, signal),
    dicts: (request, signal) => SearchService.searchDicts(request, signal),
    reports: (request, signal) => SearchService.searchReports(request, signal),
};

const abortScope = (scope: SearchScope) => {
    controllers.get(scope)?.abort();
    controllers.delete(scope);
};

export const SearchActionCreators = {
    /**
     * Единая точка выполнения поиска: диспетчеризует endpoint по `scope`, отменяет
     * предыдущий запрос области и помечает ответ id запроса. Визуальный слой не знает,
     * какой endpoint вызывать.
     */
    executeSearch:
        (request: ISearchRequest) =>
        async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
            const { scope } = request;
            const queryKey = buildSearchQueryKey(request);
            const resultSetKey = buildResultSetKey(request);
            const requestId = ++requestCounter;

            abortScope(scope);
            const controller = new AbortController();
            controllers.set(scope, controller);

            dispatch(SearchSlice.actions.searchStarted({ scope, queryKey, requestId }));

            const succeed = (items: SearchResultItem[], totalCount: number | undefined, requireTotalCount: boolean) =>
                dispatch(
                    SearchSlice.actions.searchSucceeded({
                        scope,
                        requestId,
                        queryKey,
                        resultSetKey,
                        items,
                        totalCount,
                        requireTotalCount,
                    }),
                );

            const fail = (error: unknown) => {
                if (axios.isCancel(error)) {
                    dispatch(SearchSlice.actions.searchCancelled({ scope, requestId }));
                    return;
                }
                console.error(error);
                dispatch(SearchSlice.actions.searchFailed({ scope, requestId, error: SEARCH_ERROR }));
            };

            try {
                if (scope === 'docs' && request.mode === 'ai') {
                    await aiSearchHub.start();
                    aiSearchHub.onComplete((data, success) => {
                        // Ответ хаба принадлежит уже отменённому поиску — игнорируем.
                        if (getState().search.docs.requestId !== requestId) {
                            return;
                        }
                        if (success) {
                            succeed(data?.items ?? [], data?.totalCount, true);
                        } else {
                            dispatch(SearchSlice.actions.searchFailed({ scope, requestId, error: SEARCH_ERROR }));
                        }
                    });

                    const response = await SearchService.searchDocumentsAI(request, controller.signal);
                    if (response.data.success) {
                        aiSearchHub.setRequestId(response.data.data);
                    } else {
                        aiSearchHub.stop();
                        dispatch(SearchSlice.actions.searchFailed({ scope, requestId, error: SEARCH_ERROR }));
                    }
                    return;
                }

                const response = await fetchers[scope](request, controller.signal);
                succeed(response.data.items ?? [], response.data.totalCount, request.requireTotalCount);
            } catch (error) {
                if (scope === 'docs' && request.mode === 'ai') {
                    aiSearchHub.stop();
                }
                fail(error);
            } finally {
                if (controllers.get(scope) === controller) {
                    controllers.delete(scope);
                }
            }
        },

    /** Отмена текущего запроса области — кнопка «Отменить» у длинного AI-поиска. */
    interruptSearch:
        (scope: SearchScope) =>
        (dispatch: AppDispatch, getState: () => RootState): void => {
            if (scope === 'docs') {
                aiSearchHub.stop();
            }
            abortScope(scope);
            dispatch(SearchSlice.actions.searchCancelled({ scope, requestId: getState().search[scope].requestId }));
        },

    setSearchBaseRequisitesFields:
        (fields: Record<string, IFieldElem>, forms: IForms) =>
        (dispatch: AppDispatch): void => {
            dispatch(SearchSlice.actions.setBaseRequisitesFields({ fields, forms }));
        },

    loadExtraFilterFlows:
        () =>
        async (dispatch: AppDispatch): Promise<void> => {
            try {
                dispatch(SearchSlice.actions.setExtraFlowsFilterLoading(true));
                const response = await SearchService.loadExtraFilterFlowsData();
                dispatch(SearchSlice.actions.setExtraFlowsFilterData(response.data));
            } catch (error) {
                console.error(error);
            } finally {
                dispatch(SearchSlice.actions.setExtraFlowsFilterLoading(false));
            }
        },

    selectExtraFilterFlow:
        (selectedFlow: ISearchExtraFlowSetting | undefined) =>
        (dispatch: AppDispatch): void => {
            dispatch(SearchSlice.actions.selectExtraFilterFlow(selectedFlow));
        },

    applyDocumentFilter:
        (filter: ISearchExtraBaseFilter) =>
        (dispatch: AppDispatch): void => {
            dispatch(SearchSlice.actions.applyDocumentFilter(filter));
        },

    clearDocumentFilter:
        () =>
        (dispatch: AppDispatch): void => {
            dispatch(SearchSlice.actions.clearDocumentFilter());
        },
};
